import React, { useEffect, useState } from 'react';

// An instance can drop an alternate logo into public/logos; the server reports whether
// one is there. Resolved once per page load and shared by every mount, because
// ChemotionLogo renders in both navigation bars, the workshop FAB and the sidebar --
// which remounts whenever it is collapsed or expanded.
//
// Deliberately not probed by requesting the (expectedly absent) file directly: a 404
// does not throw, but the browser still logs the failed resource load to the console on
// every stock instance. The endpoint always answers 200, so "no alternate" stays silent.
let alternateSrcPromise = null;

const fetchAlternateLogoSrc = () => {
  alternateSrcPromise ||= fetch('/api/v1/public/logo/alternate')
    .then((res) => (res.ok ? res.json() : { src: null }))
    .then(({ src }) => src || null)
    .catch(() => null);
  return alternateSrcPromise;
};

const ChemotionLogo = ({ collapsed }) => {
  const [alternateSrc, setAlternateSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAlternateLogoSrc().then((src) => { if (!cancelled) { setAlternateSrc(src); } });
    return () => { cancelled = true; };
  }, []);

  const height = '52';
  const width = collapsed ? '52' : '87';
  const scaledSize = '34';

  if (alternateSrc) {
    return (
      <img
        src={alternateSrc}
        alt="Chemotion Logo"
        style={{ height: collapsed ? Number(scaledSize) : Number(height), width: 'auto', maxWidth: '100%' }}
      />
    );
  }

  const styles = {
    blue: {
      fill: '#2495cf',
    },
    red: {
      fill: '#FF5555',
    },
    grey: {
      fill: '#4f5659',
    },
  };

  return (
    <svg
      width={collapsed ? scaledSize : width}
      height={collapsed ? scaledSize : height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Chemotion Logo</title>
      <g id="logo">
        <g id="icon">
          <path style={styles.blue} d="M26.07,40.896c-8.212,0-14.896-6.684-14.896-14.9s6.684-14.9,14.896-14.9c3.814,0,7.408,1.424,10.182,4.018l2.193-2.191c-3.356-3.178-7.729-4.923-12.373-4.923-9.923,0-17.993,8.073-17.993,17.998s8.072,17.999,17.993,17.999c4.644,0,9.018-1.744,12.371-4.923l-2.195-2.191c-2.768,2.59-6.361,4.012-10.178,4.012"/>
          <path style={styles.grey} d="M41.424,42.417h0c-4.031,3.787-9.456,6.107-15.424,6.107-12.442,0-22.526-10.086-22.526-22.526S13.558,3.472,26,3.472c5.935,0,11.335,2.295,15.357,6.048h0l2.332-2.574C39.049,2.636,32.832,0,26,0,11.641,0,0,11.641,0,26s11.641,26,26,26c6.864,0,13.105-2.659,17.752-7.004l.002-.002-2.331-2.578h0Z"/>
          <path style={styles.red} d="M29.652,28.071l1.815.458c-.381,1.491-1.065,2.629-2.054,3.41s-2.197,1.175-3.625,1.175c-1.479,0-2.68-.3-3.605-.902-.928-.602-1.632-1.473-2.113-2.615-.484-1.142-.726-2.366-.726-3.676,0-1.428.273-2.674.819-3.737s1.322-1.871,2.331-2.423c1.007-.552,2.115-.828,3.325-.828,1.372,0,2.525.351,3.462,1.048.934.699,1.587,1.682,1.955,2.947l-1.786.422c-.318-.999-.78-1.724-1.383-2.179-.606-.455-1.366-.683-2.281-.683-1.055,0-1.935.253-2.643.759s-1.206,1.185-1.492,2.034c-.287.851-.43,1.73-.43,2.635,0,1.167.171,2.185.508,3.055.341.871.869,1.521,1.585,1.95.718.43,1.495.646,2.331.646,1.016,0,1.877-.293,2.581-.878.7-.59,1.179-1.461,1.428-2.615l-.002-.002h0Z"/>
        </g>
        {!collapsed && (
          <g id="full-name">
            <g style={styles.red}>
              <path d="M35.91,22.063v3.143c.528-.618,1.161-.928,1.895-.928.377,0,.718.07,1.02.209.304.139.532.32.685.536.153.218.259.458.315.724.056.265.085.675.085,1.231v3.635h-1.639v-3.273c0-.648-.031-1.061-.092-1.236s-.172-.314-.329-.416-.355-.155-.592-.155c-.273,0-.515.066-.73.198s-.369.331-.471.598c-.098.267-.149.659-.149,1.181v3.102h-1.639v-8.55h1.642v.002h-.002Z"/>
              <path d="M45.161,28.641l1.634.275c-.209.598-.542,1.055-.994,1.368s-1.02.471-1.701.471c-1.078,0-1.874-.352-2.392-1.057-.409-.563-.612-1.276-.612-2.134,0-1.026.269-1.83.805-2.411s1.215-.873,2.036-.873c.922,0,1.648.304,2.181.914s.787,1.54.765,2.796h-4.106c.011.486.143.865.396,1.134.253.27.567.404.944.404.257,0,.472-.07.648-.209.174-.141.306-.365.396-.677h0v-.002ZM45.253,26.984c-.011-.475-.133-.834-.368-1.081s-.516-.369-.851-.369c-.358,0-.654.13-.887.391-.234.261-.349.614-.345,1.061h2.451Z"/>
              <path d="M48.094,24.418h1.511v.846c.54-.658,1.185-.985,1.931-.985.396,0,.741.081,1.032.245s.53.41.718.741c.273-.331.565-.577.881-.741.315-.163.652-.245,1.008-.245.455,0,.84.092,1.155.277s.549.457.706.812c.112.265.168.693.168,1.283v3.959h-1.639v-3.539c0-.614-.056-1.01-.168-1.191-.151-.234-.385-.351-.7-.351-.23,0-.445.07-.648.209s-.349.345-.437.615c-.089.27-.133.696-.133,1.281v2.974h-1.639v-3.394c0-.602-.029-.991-.087-1.167-.058-.174-.149-.306-.27-.391s-.288-.127-.5-.127c-.253,0-.481.068-.683.204-.202.136-.347.333-.436.588-.087.257-.132.683-.132,1.277v3.01h-1.639v-6.191h.002-.002,0Z"/>
              <path d="M58.458,27.427c0-.543.133-1.072.402-1.581s.648-.898,1.14-1.167,1.041-.402,1.646-.402c.936,0,1.705.304,2.303.914s.898,1.378.898,2.307c0,.936-.302,1.714-.908,2.331-.604.618-1.366.926-2.284.926-.567,0-1.109-.127-1.624-.385s-.908-.633-1.175-1.128-.4-1.099-.4-1.811h0v-.002ZM60.138,27.514c0,.614.145,1.085.437,1.41s.652.49,1.079.49.786-.163,1.075-.49.436-.801.436-1.423c0-.606-.145-1.074-.436-1.399-.29-.328-.648-.49-1.075-.49s-.787.163-1.079.49-.437.797-.437,1.41h0v.002Z"/>
              <path d="M68.973,24.418v1.307h-1.12v2.497c0,.505.01.799.033.885.021.082.07.153.145.208s.168.081.277.081c.151,0,.371-.052.659-.157l.139,1.271c-.381.163-.812.245-1.294.245-.296,0-.561-.051-.799-.149s-.41-.228-.522-.385c-.11-.157-.188-.369-.23-.639-.035-.19-.052-.574-.052-1.155v-2.7h-.753v-1.307h.753v-1.231l1.645-.956v2.187h1.12v-.002h0Z"/>
              <path d="M70.112,23.578v-1.517h1.639v1.517h-1.639ZM70.112,30.613v-6.195h1.639v6.195h-1.639Z"/>
              <path d="M73.052,27.427c0-.543.133-1.072.402-1.581s.648-.898,1.14-1.167,1.041-.402,1.646-.402c.936,0,1.705.304,2.303.914s.898,1.378.898,2.307c0,.936-.302,1.714-.908,2.331-.604.618-1.366.926-2.284.926-.567,0-1.109-.127-1.624-.385s-.908-.633-1.175-1.128-.4-1.099-.4-1.811h0v-.002ZM74.731,27.514c0,.614.145,1.085.437,1.41s.652.49,1.079.49.786-.163,1.075-.49.436-.801.436-1.423c0-.606-.145-1.074-.436-1.399-.29-.328-.648-.49-1.075-.49s-.787.163-1.079.49-.437.797-.437,1.41h0v.002Z"/>
              <path d="M86.36,30.613h-1.639v-3.161c0-.669-.035-1.101-.105-1.298-.07-.196-.184-.349-.341-.458-.157-.108-.347-.163-.569-.163-.284,0-.538.077-.765.234-.226.155-.379.361-.465.618-.082.257-.126.731-.126,1.423v2.806h-1.639v-6.195h1.523v.91c.54-.7,1.221-1.049,2.042-1.049.361,0,.693.066.991.196.3.13.526.296.679.5.153.202.261.431.321.689s.091.624.091,1.103v3.847h0v-.002h0Z"/>
            </g>
            <g>
              <path d="M74.851,37.175v-4.294h3.184v.726h-2.317v.952h2.156v.724h-2.156v1.169h2.399v.724h-3.267,0Z"/>
              <path d="M78.876,37.175v-4.259h.867v3.536h2.156v.724h-3.023Z"/>
              <path d="M82.526,37.175v-4.294h.843l1.758,2.868v-2.868h.805v4.294h-.871l-1.732-2.8v2.8h-.803Z"/>
            </g>
          </g>
        )}
      </g>
    </svg>
  );
};

export default ChemotionLogo;
